import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AdminPageHeader, Form, AdminFormAction } from '@ui';
import {
  DEFAULT_TX_CENTER_VALUES,
  NewTXCenterForm,
  useTxCenterFormSchema,
  TXCentersForm,
} from '@lp/features/admin/forms/TXCentersForm';
import { PrimaryTXUserForm } from '@lp/features/admin/forms/PrimaryTXUserForm';
import { AuthContext } from '@lp/core/auth';
import { useContext } from 'react';
import { useCreateTXCenterMutation } from '@/queries/tx-centers';
import { useCreateCustomerMutation } from '@/queries/customer';
import { useUploadFile } from '@/hooks/useUploadFile';
import { retrieveUnifiedError } from '@/api/utils';
import { AdminPermissions } from '@/permissions';
import { FileType } from '@/queries/file';
import { convertToIanaTimezone } from '@/utils/helpers';
import { CustomerRoleT } from '@/api/apis/customer';

export const TXCentersCreate = () => {
  const navigate = useNavigate();
  const { mutateAsync } = useCreateTXCenterMutation();
  const { mutateAsync: createCustomerAsync } = useCreateCustomerMutation();
  const { uploadFiles } = useUploadFile(FileType.Facility);
  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.TxCenterCreate);
  const hasQadCustomerIdPermissions = checkPermission(AdminPermissions.TxCenterUpdateQadCustomerId);
  const { schema, isLoading: isGeoDataLoading } = useTxCenterFormSchema();

  const handleBackToList = () => {
    navigate('/admin/customers/tx_centers');
  };

  const handleSubmit = async (data: NewTXCenterForm) => {
    let showAdditionalToastMsg = false;

    try {
      let uploadedTxCenterLogo;
      let uploadedCustomerPhoto;

      if (data.image) {
        uploadedTxCenterLogo = await uploadFiles([data.image]);
      }
      if (data.photo) {
        uploadedCustomerPhoto = await uploadFiles([data.photo]);
      }

      const hasTrialPrograms = !!data.trialProgramIds?.length;

      const txCenter = await mutateAsync({
        name: data.name,
        code: data.code,
        timezone: data.timezoneIANA || convertToIanaTimezone(data.timezone),
        logoPath: uploadedTxCenterLogo ? uploadedTxCenterLogo[0].fileName : undefined,
        addressLine: data.addressLine,
        city: data.city,
        state: data.state,
        postalCode: data.postalCode,
        country: data.country,
        longitude: data.longitude,
        latitude: data.latitude,
        notes: data.notes,
        hasFixedPriceContract: data.hasFixedPriceContract,
        trialProgramIds: hasTrialPrograms ? data.trialProgramIds : null,
        trialProgramNotes: data.trialProgramNotes,
        qadCustomerId: data.qadCustomerId || null,
      });

      if (txCenter) {
        showAdditionalToastMsg = true;
      }

      const rolesAndPermissions: CustomerRoleT[] = Array.from(
        new Set([...(data.roles ?? []), ...(data.permissions ?? [])]),
      );

      await createCustomerAsync({
        photoPath: uploadedCustomerPhoto ? uploadedCustomerPhoto[0]?.fileName : undefined,
        txCenterId: txCenter.id,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        primaryPhone: data.primaryPhone,
        secondaryPhone: data.secondaryPhone === '' ? undefined : data.secondaryPhone,
        roles: rolesAndPermissions,
      });

      showAdditionalToastMsg = false;

      toast.success(`TX Center and its primary TX User created successfully.`);

      handleBackToList();
    } catch (e) {
      if (showAdditionalToastMsg) {
        toast.success(`TX Center created successfully.`);
      }

      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  if (isGeoDataLoading) return null;

  return (
    <Form<NewTXCenterForm> defaultValues={DEFAULT_TX_CENTER_VALUES} schema={schema} onSubmit={handleSubmit}>
      <div className="flex flex-col gap-9">
        <AdminPageHeader title="Create New TX Center" backToListLink="/admin/customers/tx_centers">
          <AdminFormAction type="create" handleClick={handleBackToList} hasCreatePermissions={hasCreatePermissions} />
        </AdminPageHeader>
        <TXCentersForm isReadOnly={!hasCreatePermissions} isQadCustomerIdReadOnly={!hasQadCustomerIdPermissions} />
        <PrimaryTXUserForm isDisabled={!hasCreatePermissions} />
      </div>
    </Form>
  );
};
